import type React from 'react';

import type { ShortcutType } from 'lib/keyboardShortcuts';

export type MonarchCommandGroup =
  | 'general'
  | 'settings'
  | 'actions'
  | 'navigation'
  | 'miscellaneous'
  | 'entities';

type MonarchCommandActionOptions = {
  /** Combo used to trigger the command, if applicable. */
  combo: string;
};

export interface MonarchCommand {
  /**
   * The ID of the command.
   *
   * This should be unique across the app.
   */
  id: string;
  label: string | React.ReactNode;
  /**
   * The group to which the command belongs.
   * Make sure the group is rendered in the CommandPalette component, otherwise it won't be shown.
   */
  group?: MonarchCommandGroup;
  /**
   * The score to rank the command in the results list.
   */
  score?: number;
  /**
   * The keys to trigger the command.
   * If not provided, the command will not be triggered by any keys.
   *
   * We use Mousetrap to handle the key bindings. More at: https://craig.is/killing/mice
   *
   * @example
   * ```ts
   * ['command+shift+p', 'ctrl+shift+p']
   * ```
   */
  keys?: string[];
  shortcutType?: ShortcutType;
  icon?: React.ReactNode;
  /**
   * The action to perform when the command is triggered.
   *
   * Can return a boolean to indicate if the command was executed successfully.
   * If the command was not executed properly, it will not be tracked in analytics.
   */
  action: (
    options: Partial<MonarchCommandActionOptions>,
  ) => boolean | void | Promise<boolean | void>;
  /**
   * The keywords to improve searchability of the command.
   *
   * @example
   * ```ts
   * ['command', 'palette', 'open']
   * ```
   */
  keywords?: string[];
  /**
   * Additional condition that must be met for the command to be registered.
   */
  shouldRegister?: boolean;
  /**
   * Additional metadata to be sent to analytics.
   *
   * You are required to pass in an object with an `analyticsId` property.
   * This is used to identify the command in analytics without creating a high cardinality event.
   */
  analyticsMetadata: {
    /**
     * The analytics ID for the command.
     *
     * This doesn't need to be unique across the app like the `id` property, and it's used
     * to identify the command in analytics without creating a high cardinality event.
     *
     * @example
     * ```ts
     * {
     *   analyticsId: 'transaction-drawer.mark-as-reviewed',
     *   entityType: 'transaction',
     *   entityId: '123',
     * }
     * ```
     */
    analyticsId: string;
    /**
     * Additional metadata to be sent to analytics.
     */
    [key: string]: unknown;
  };
}

export interface MonarchAiCommand extends MonarchCommand {
  isAi: true;
  icon: never;
}

export interface MonarchEntityCommand extends MonarchCommand {
  type: string;
}

export interface MonarchCommandView extends Omit<MonarchCommand, 'action'> {
  hideLabel?: boolean;
  component: React.FC<MonarchCommandViewComponentProps>;
}
export type MonarchCommandViewComponentProps = {
  close: () => void;
};

/** Group of commands that are known to the command palette. */
export type MonarchKnownCommand =
  | MonarchCommand
  | MonarchEntityCommand
  | MonarchAiCommand
  | MonarchCommandView;

export function isMonarchAiCommand(command: MonarchKnownCommand): command is MonarchAiCommand {
  return Object.hasOwn(command, 'isAi');
}

export function isMonarchEntityCommand(
  command: MonarchKnownCommand,
): command is MonarchEntityCommand {
  return Object.hasOwn(command, 'type');
}

export function isMonarchCommandView(command: MonarchKnownCommand): command is MonarchCommandView {
  return Object.hasOwn(command, 'component');
}
